/**
 * Which voice changes play which sound: a pure diff of two voice sessions
 * (tested on its own), and the subscription that plays it.
 */
import type { Snowflake } from '@neosocial/protocol';
import type { VoiceSession } from '../voice/machine';
import { useVoice } from '../voice/store';
import { play, setDeafenedProbe, type SoundEvent } from './sounds';

const live = (s: VoiceSession) => s.status === 'connected';
/** The connection dropped: failed for good, or reconnecting by itself (HV-B01). */
const dropped = (s: VoiceSession) => s.status === 'failed' || s.reconnecting > 0;

/** Who is live (sharing a screen) in my channel, by pid (me included). */
function roomScreens(s: VoiceSession): Set<number> {
  const out = new Set<number>();
  if (s.pid === null || !s.channelId) return out;
  for (const m of Object.values(s.members))
    if (m.channel === s.channelId && m.screen) out.add(m.pid);
  return out;
}

/** I am sharing my screen (the roster says so). */
const imLive = (s: VoiceSession) => s.pid !== null && !!s.members[s.pid]?.screen;

/** Who watches my own stream, by pid. */
function myViewers(s: VoiceSession): Set<number> {
  if (s.pid === null) return new Set();
  return new Set((s.viewers[s.pid] ?? []).filter((p) => p !== s.pid));
}

/** Who is in my channel, by user (my own user excluded). */
function roomUsers(s: VoiceSession): Set<Snowflake> {
  const out = new Set<Snowflake>();
  if (s.pid === null || !s.channelId) return out;
  const me = s.members[s.pid]?.user_id;
  for (const m of Object.values(s.members))
    if (m.channel === s.channelId && m.user_id !== me) out.add(m.user_id);
  return out;
}

export interface SoundContext {
  /** Push-to-talk is the input mode (voice activity gates too often to click). */
  ptt: boolean;
  /** The last session was lost while connected, so the next connect is a reconnect. */
  wasLost: boolean;
}

/** The sounds a change from `a` to `b` plays, in order. */
export function voiceSounds(a: VoiceSession, b: VoiceSession, o: SoundContext): SoundEvent[] {
  const out: SoundEvent[] = [];
  // you: connected, moved, left, lost, back
  if (!live(a) && live(b)) out.push(o.wasLost ? 'reconnected' : 'join');
  else if (live(a) && dropped(b)) out.push('disconnected');
  else if (live(a) && b.status === 'idle') out.push('leave');
  else if (live(a) && live(b) && a.channelId !== b.channelId) out.push('move');
  // others joining or leaving your channel (not your own join, move or leave)
  if (live(a) && live(b) && a.channelId === b.channelId && a.pid === b.pid) {
    const before = roomUsers(a);
    const after = roomUsers(b);
    for (const u of after) if (!before.has(u)) out.push('peerJoin');
    for (const u of before) if (!after.has(u)) out.push('peerLeave');
    // streams (2026-11-02): one starting or ending in your room, yours too,
    // and a viewer of yours starting or stopping; someone who came or went
    // has their own join or leave sound, never a stream sound on top
    const stayed = (pid: number) => !!a.members[pid] && b.members[pid]?.channel === b.channelId;
    const was = roomScreens(a);
    const now = roomScreens(b);
    for (const p of now) if (!was.has(p) && stayed(p)) out.push('streamStart');
    for (const p of was) if (!now.has(p) && stayed(p)) out.push('streamEnd');
    // viewers only while my stream runs on (its start or end says that alone)
    if (imLive(a) && imLive(b)) {
      const seen = myViewers(a);
      const sees = myViewers(b);
      for (const p of sees) if (!seen.has(p) && stayed(p)) out.push('viewerJoin');
      for (const p of seen) if (!sees.has(p) && stayed(p)) out.push('viewerLeave');
    }
  }
  // your controls (deafening covers the mute it implies)
  if (a.selfDeaf !== b.selfDeaf) out.push(b.selfDeaf ? 'deafen' : 'undeafen');
  else if (a.selfMute !== b.selfMute) out.push(b.selfMute ? 'mute' : 'unmute');
  if (o.ptt && live(a) && live(b) && a.transmitting !== b.transmitting)
    out.push(b.transmitting ? 'pttOn' : 'pttOff');
  return [...new Set(out)];
}

let off: (() => void) | null = null;

/** Plays voice sounds for the whole app (called once at start; again re-binds). */
export function startSoundWiring(): void {
  off?.();
  setDeafenedProbe(() => useVoice.getState().session.selfDeaf);
  let wasLost = false;
  off = useVoice.subscribe((st, prev) => {
    const a = prev.session;
    const b = st.session;
    if (a === b) return;
    for (const ev of voiceSounds(a, b, { ptt: st.prefs.inputMode === 'ptt', wasLost })) play(ev);
    if (live(a) && dropped(b)) wasLost = true;
    else if (live(b) || b.status === 'idle') wasLost = false;
  });
}
